import moment from "moment";
import { compact } from "lodash";
import { computed, observable } from "mobx";
import {
  BookingSupportStatusDto,
  BookingTotalsResultDto,
  InvoiceMonth,
  InvoiceSplitType,
  timeReportService
} from "@api/timereports";
import { globalStore, store } from "@store";
import {
  CONTRACT_SLIDER,
  ContractViewLocation,
  CUSTOMER_INVOICE_SLIDER,
  CustomerInvoiceLocation,
  SICK_FORM_SLIDER
} from "@navigation";
import {
  BookingInvoicing_Fetch,
  BookingInvoicingAssignBooking,
  BookingInvoicingBookingFragment,
  BookingKitType,
  CheckFortnoxSalaryForWeek,
  PublishPayFormRecordsToFortnox,
  PublishSickPayFormToFortnox,
  PublishSubcontractorInvoice,
  SetBookingAdditionalPayout,
  Timereport_GetInvoiceStatusForBooking
} from "@graphql";
import { autobind, dateToWeekYear } from "@bonliva-crm/common";
import { ConsultantPaymentFormLocation } from "@navigation/locations/ConsultantPaymentFormLocation";
import { SubcontractorFormLocation } from "@navigation/locations/SubcontractorFormLocation";
import { SickFormLocation } from "@navigation/locations/SickFormLocation";
import { openInNewWindow } from "@utils/openInNewWindow";
import { processDownloaded, processViewInNewTab } from "@api";
import { Option } from "@common/Select";
import locale from "@bonliva-crm/locale";
import { FormStore } from "@modules/formx";
import { environment } from "../../../environments/environment";

export const payFormWeekDayOptions: Option[] = [
  { value: "1", label: locale.WeekDays.Monday },
  { value: "2", label: locale.WeekDays.Tuesday },
  { value: "3", label: locale.WeekDays.Wednesday },
  { value: "4", label: locale.WeekDays.Thursday },
  { value: "5", label: locale.WeekDays.Friday },
  { value: "6", label: locale.WeekDays.Saturday },
  { value: "0", label: locale.WeekDays.Sunday }
];

export class BookingInvoicingStore {
  @observable public error = false;
  @observable public loading = false;
  @observable public totalsLoading = false;
  @observable public totalsError = false;
  @observable public isCreatingFortnoxInvoice = false;
  @observable public isSendingPaymentToFortnox = false;
  @observable public isSendingSickFormToFortnox = false;
  @observable public isAssigningToBooking = false;
  @observable public showPayFormSelectDayDialog = false;
  @observable public showSickFormSelectDayDialog = false;
  @observable public showFortnoxInvoiceDialog = false;
  @observable public payFormWeekDay = 2;
  @observable public sickFormWeekDay = 2;
  @observable public takenWeekDays: number[] = [];
  @observable public isCheckingFortnox = false;
  fortnoxInvoiceForm: FormStore<{
    month: InvoiceMonth;
    type: InvoiceSplitType;
  }>;

  @observable private _data: BookingSupportStatusDto | null = null;
  @observable private _bookingData: BookingInvoicingBookingFragment | null = null;
  @observable private _totalsData: BookingTotalsResultDto | null = null;

  constructor(
    public readonly bookingId: string,
    public readonly onFormClosed: () => void,
    public readonly bookingDate,
    public readonly consultantFirstName,
    public readonly consultantLastName,
    public readonly isGroupInvoicing: boolean
  ) {
    autobind(this);
    this.fetch();

    this.fortnoxInvoiceForm = new FormStore<{
      month: InvoiceMonth;
      type: InvoiceSplitType;
    }>({
      // schema,
      // onSubmit: this.onSubmit,
      initialValues: {
        month: InvoiceMonth.Full,
        type: InvoiceSplitType.ByShift
      }
    });
    (window as any).bookingInvoiceStore = this;
  }

  get allowed() {
    return (
      globalStore().user.can("booking-invoice-form:read:all") ||
      globalStore().user.can("invoice:read:all") ||
      globalStore().user.can("booking-pay-form:read:all")
    );
  }

  get enabled() {
    return !this.isCreatingFortnoxInvoice;
  }

  get data(): BookingSupportStatusDto | null {
    return this._data;
  }

  get bookingData(): BookingInvoicingBookingFragment | null {
    return this._bookingData;
  }

  get totals(): BookingTotalsResultDto | null {
    return this._totalsData;
  }

  get showPayFormAdditionalPayoutInput() {
    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      this.enabled &&
      !this._data?.paymentFormExists
    );
  }

  get showSubcontractorAdditionalPayoutInput() {
    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      this.enabled &&
      !this._data?.subcontractorFormExists
    );
  }

  get showSummary() {
    return (
      globalStore().user.can("booking-totals:read:own") &&
      ((this._data?.invoiceFormExists &&
        (this._data?.paymentFormExists || this._data?.subcontractorFormExists)) ||
        this._data?.sickFormExists)
    );
  }

  get canDownloadInvoiceForm() {
    return (
      globalStore().user.can("booking-invoice-form:read:all") &&
      this.enabled &&
      this._data &&
      this._data.invoiceFormExists
    );
  }

  get canCreatePaymentForm() {
    const enabledByBookingKit = checkDocumentEditEnabled(
      this._bookingData,
      "consultantPay"
    );

    console.log("canCreatePaymentForm", {
      permission: globalStore().user.can("booking-pay-form:create:all"),
      enabled: this.enabled,
      enabledByBookingKit,
      canCreatePaymentForm: this._data?.canCreatePaymentForm
    });

    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      this.enabled &&
      this._data?.canCreatePaymentForm &&
      enabledByBookingKit
    );
  }

  get canCreateSickForm() {
    const enabledByBookingKit = checkDocumentEditEnabled(this._bookingData, "sickPay");

    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      enabledByBookingKit &&
      this._data?.canCreateSickForm
    );
  }

  get sickTimeExist() {
    return (
      globalStore().user.can("booking-pay-form:create:all") && this._data?.hasSickTime
    );
  }

  get canDownloadPaymentForm() {
    return (
      globalStore().user.can("booking-pay-form:read:all") &&
      this.enabled &&
      this._data?.paymentFormExists
    );
  }

  get canDownloadSickForm() {
    return (
      globalStore().user.can("booking-pay-form:read:all") &&
      this.enabled &&
      this._data?.sickFormExists
    );
  }

  get canSendPaymentToFortnox() {
    return (
      globalStore().user.can("invoice:write:all") &&
      this.enabled &&
      this._data?.paymentFormExists
    );
  }

  get canSendSickFormToFortnox() {
    return (
      globalStore().user.can("invoice:write:all") &&
      this.enabled &&
      this._data?.sickFormExists
    );
  }

  @computed get payFormPublishedToFortnox(): string {
    return this._data?.payFormPublishedToFortnox
      ? moment(this._data?.payFormPublishedToFortnox).format("DD/MM")
      : "n/a";
  }

  @computed get sickFormPublishedToFortnox(): string {
    return this._data?.sickFormPublishedToFortnox
      ? moment(this._data?.sickFormPublishedToFortnox).format("DD/MM")
      : "n/a";
  }

  get canCreateSubcontractorForm() {
    const enabledByBookingKit = checkDocumentEditEnabled(
      this._bookingData,
      "subcontractorPay"
    );

    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      this.enabled &&
      enabledByBookingKit &&
      this._data?.canCreateSubcontractorForm
    );
  }

  get canSendSubcontractorFormToRecipient() {
    return (
      globalStore().user.can("booking-pay-form:create:all") &&
      this.enabled &&
      this._data?.subcontractorFormExists
    );
  }

  get canDownloadSubcontractorForm() {
    return (
      globalStore().user.can("booking-pay-form:read:all") &&
      this.enabled &&
      this._data?.subcontractorFormExists
    );
  }

  get canOpenSubcontractorFormInFortnox() {
    return (
      globalStore().user.can("invoice:read:all") &&
      this.enabled &&
      this._data?.subcontractorFormExists &&
      this.data.subcontractFormInFortnoxUrl
    );
  }

  get canSendSubcontractoFormToFortnox() {
    return (
      globalStore().user.can("invoice:write:all") &&
      this.enabled &&
      !this._data?.subcontractFormExistsInFortnox &&
      this._data?.subcontractorFormExists
    );
  }

  get canCreateInvoice() {
    if (this._bookingData?.unlockForms) {
      return true;
    }

    const enabledByBookingKit = checkDocumentEditEnabled(
      this._bookingData,
      "customerInvoice"
    );

    console.log("canCreateInvoice", {
      permission: globalStore().user.can("booking-invoice-form:create:all"),
      enabled: this.enabled,
      enabledByBookingKit
    });

    return (
      globalStore().user.can("booking-invoice-form:create:all") &&
      this.enabled &&
      this._data?.canCreateInvoiceForm &&
      enabledByBookingKit &&
      !this._bookingData?.IsCustomerBilled
    );
  }

  get canCreateFortnoxInvoice() {
    return (
      globalStore().user.can("invoice:write:all") &&
      this.enabled &&
      this._data?.invoiceFormExists
      // &&
      // !this._data?.fortnoxInvoiceExists
    );
  }

  get canPreviewFortnoxInvoice() {
    return (
      globalStore().user.can("invoice:read:all") &&
      this.enabled &&
      this._data?.fortnoxInvoiceIds?.length > 0
    );
  }

  get canAssignToBooking() {
    return (
      globalStore().user.can("invoice:write:all") &&
      this.enabled &&
      this._data?.fortnoxInvoiceIds?.length > 0
    );
  }

  get canSeeAllTotals() {
    return globalStore().user.can("booking-totals:read:all");
  }

  fortnoxInvoiceUrl(id) {
    return environment.fortnoxWebUrl + `/kf/invoice/${id}`;
  }

  fortnoxEmployeeUrl(employeeNumber: string) {
    return (
      environment.fortnoxWebUrl +
      `/lon/personal/${employeeNumber}/sub/personal-information`
    );
  }

  async fetch() {
    try {
      this.loading = true;

      const bookingResult = await BookingInvoicing_Fetch.func(
        {
          id: this.bookingId
        },
        { fetchPolicy: "network-only" }
      );

      this._bookingData = bookingResult.data.bookingById;

      const timereportResult = await Timereport_GetInvoiceStatusForBooking.func(
        { bookingId: this.bookingId },
        { fetchPolicy: "network-only" }
      );

      this._data =
        timereportResult?.data?.getInvoiceStatusForBooking?.invoicePaymentStatuses;
    } catch (err) {
      console.log(err);
      globalStore().snackbars.error("Cannot fetch invoice status");
      this.error = err.toString();
    } finally {
      this.loading = false;
    }
  }

  fetchTotals() {
    this.totalsLoading = true;

    timeReportService
      .getBookingTotal({ bookingId: this.bookingId })
      .then((result) => {
        this._totalsData = result.data;
      })
      .catch(() => {
        globalStore().snackbars.error("Cannot fetch booking totals");
        this.totalsError = true;
      })
      .finally(() => {
        this.totalsLoading = false;
      });
  }

  goToInvoiceForm() {
    globalStore()
      .navigation.open(
        CUSTOMER_INVOICE_SLIDER + Math.random(),
        new CustomerInvoiceLocation(this.bookingId)
      )
      .afterClose.on(() => {
        this.fetch();
        this.onFormClosed();
      });
  }

  goToPaymentForm() {
    globalStore()
      .navigation.open(
        CUSTOMER_INVOICE_SLIDER + Math.random(),
        new ConsultantPaymentFormLocation(this.bookingId)
      )
      .afterClose.on(() => {
        this.fetch();
        this.onFormClosed();
      });
  }

  goToSubcontractorForm() {
    if (!this.canCreateSubcontractorForm) {
      return;
    }

    globalStore()
      .navigation.open(
        CUSTOMER_INVOICE_SLIDER + Math.random(),
        new SubcontractorFormLocation(this.bookingId)
      )
      .afterClose.on(() => {
        this.fetch();
        this.onFormClosed();
      });
  }

  async handleSendPayFormClick(e) {
    e.stopPropagation();
    this.isCheckingFortnox = true;
    try {
      const { data } = await CheckFortnoxSalaryForWeek.func(
        {
          bookingId: this.bookingId
        },
        { fetchPolicy: "network-only" }
      );
      const taken = data?.checkFortnoxSalaryForWeek?.takenWeekDays ?? [];
      this.takenWeekDays = taken;
      if (!taken.includes(1)) {
        store().ui.confirmNullify(locale.SendToFortnox, (confirmed) => {
          if (confirmed) this.sendPaymentFormToFortnox(e);
        });
      } else {
        this.togglePayFormWeekDayDialog();
      }
    } catch {
      globalStore().snackbars.error("Något gick fel vid kontroll av Fortnox");
    } finally {
      this.isCheckingFortnox = false;
    }
  }

  async handleSendSickFormClick(e) {
    e.stopPropagation();
    this.isCheckingFortnox = true;
    try {
      const { data } = await CheckFortnoxSalaryForWeek.func(
        {
          bookingId: this.bookingId
        },
        { fetchPolicy: "network-only" }
      );
      const taken = data?.checkFortnoxSalaryForWeek?.takenWeekDays ?? [];
      this.takenWeekDays = taken;
      if (!taken.includes(1)) {
        store().ui.confirmNullify(locale.SendToFortnox, (confirmed) => {
          if (confirmed) this.sendSickFormToFortnox(e);
        });
      } else {
        this.toggleSickFormWeekDayDialog();
      }
    } catch {
      globalStore().snackbars.error("Något gick fel vid kontroll av Fortnox");
    } finally {
      this.isCheckingFortnox = false;
    }
  }

  sendPaymentFormToFortnox(e, weekDay?) {
    this.isSendingPaymentToFortnox = true;
    PublishPayFormRecordsToFortnox.func({
      bookingId: this.bookingId,
      weekDay: weekDay ?? 1
    })
      .then(({ data }) => {
        globalStore().workflowTasks.addTask({
          workflowId: data.publishPayFormRecordsToFortnox.workflowId,
          title: "Löneformulär till Fortnox",
          bookingId: this.bookingId,
          metadata: data.publishPayFormRecordsToFortnox.metadata
        });
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.description || data?.name) {
          globalStore().snackbars.error(`${data?.name}\n${data?.description}`);
          return;
        }

        globalStore().snackbars.error(err?.message || "Något gick fel");
      })
      .finally(() => {
        this.isSendingPaymentToFortnox = false;
      });
  }

  sendSickFormToFortnox(e, weekDay?) {
    this.isSendingSickFormToFortnox = true;

    PublishSickPayFormToFortnox.func({
      bookingId: this.bookingId,
      weekDay: weekDay ?? 1
    })
      .then(({ data }) => {
        globalStore().workflowTasks.addTask({
          workflowId: data.publishSickPayFormToFortnox.workflowId,
          title: "Sjuklöneformulär till Fortnox",
          bookingId: this.bookingId,
          metadata: data.publishSickPayFormToFortnox.metadata
        });
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.description || data?.name) {
          globalStore().snackbars.error(`${data?.name}\n${data?.description}`);
          return;
        }

        globalStore().snackbars.error(err?.message || "Något gick fel");
      })
      .finally(() => {
        this.isSendingSickFormToFortnox = false;
      });
  }

  createFortnoxInvoice(e, force = false) {
    e?.stopPropagation();

    if (this.isCreatingFortnoxInvoice) {
      return;
    }

    this.isCreatingFortnoxInvoice = true;
    const { type, month } = this.fortnoxInvoiceForm.values;

    // The API refuses a second invoice for a period it has already invoiced, so the operator is
    // asked to confirm before it is forced through - after cancelling the first one in Fortnox.
    let alreadyExists: { description?: string } = null;

    timeReportService
      // Left out rather than sent as false, so the request stays what it was before `force`
      // became a generated parameter: the endpoint lives in a dependency, not in this repo.
      .createInvoiceForBooking({
        bookingId: this.bookingId,
        month,
        type,
        force: force || undefined
      })
      .then(() => {
        return BookingInvoicingAssignBooking.func({
          bookingId: this.bookingId,
          isGroupInvoicing: this.isGroupInvoicing
        });
      })
      .then(() => {
        globalStore().snackbars.success("Levererat till Fortnox");
      })
      .catch((err) => {
        if (err.isAxiosError) {
          const data = err.response.data;

          if (!force && data?.name === "invoice already exists") {
            alreadyExists = data;
            return;
          }

          globalStore().snackbars.error(`${data.name}<br>${data.description}`);
        } else {
          console.error(err);
          globalStore().snackbars.error(`Något gick fel`);
        }
      })
      .finally(() => {
        this.isCreatingFortnoxInvoice = false;
        this.fetch();

        if (alreadyExists) {
          store().ui.confirmNullify(
            `${alreadyExists.description} Skapa en till?`,
            (confirmed) => {
              if (confirmed) this.createFortnoxInvoice(null, true);
            }
          );
        }
      });
  }

  appendInvoiceFormToFortnox(
    invoiceNumber: string,
    month: InvoiceMonth,
    type: InvoiceSplitType,
    force = false
  ) {
    if (this.isCreatingFortnoxInvoice) {
      return;
    }

    this.isCreatingFortnoxInvoice = true;

    // Same period guard as createFortnoxInvoice, so it needs the same way out: without this the
    // operator who cancelled the invoice in Fortnox is left with an error and no way to continue.
    let alreadyExists: { description?: string } = null;

    timeReportService
      .appendInvoiceFormToFortnoxInvoice({
        bookingId: this.bookingId,
        invoiceNumber,
        month,
        type,
        force: force || undefined
      })
      .then(() => {
        return BookingInvoicingAssignBooking.func({
          bookingId: this.bookingId,
          isGroupInvoicing: this.isGroupInvoicing
        });
      })
      .then(() => {
        globalStore().snackbars.success("Levererat till Fortnox");
      })
      .catch((err) => {
        if (err.isAxiosError) {
          const data = err.response.data;

          if (!force && data?.name === "invoice already exists") {
            alreadyExists = data;
            return;
          }

          globalStore().snackbars.error(`${data.name}<br>${data.description}`);
        } else {
          console.error(err);
          globalStore().snackbars.error(`Något gick fel`);
        }
      })
      .finally(() => {
        this.isCreatingFortnoxInvoice = false;
        this.fetch();

        if (alreadyExists) {
          store().ui.confirmNullify(
            `${alreadyExists.description} Lägg till på fakturan ändå?`,
            (confirmed) => {
              if (confirmed) {
                this.appendInvoiceFormToFortnox(invoiceNumber, month, type, true);
              }
            }
          );
        }
      });
  }

  appendSubcontractorInvoiceFormToFortnox(invoiceNumber: string) {
    if (this.isCreatingFortnoxInvoice) {
      return;
    }

    this.isCreatingFortnoxInvoice = true;

    timeReportService
      .appendSubcontractorFormDraftToFortnoxInvocie({
        bookingId: this.bookingId,
        invoiceNumber
      })
      .then(() => {
        globalStore().snackbars.success("Levererat till Fortnox");
      })
      .catch((err) => {
        if (err.isAxiosError) {
          const data = err.response.data;
          globalStore().snackbars.error(`${data.name}<br>${data.description}`);
        } else {
          globalStore().snackbars.error(`Något gick fel`);
        }
      })
      .finally(() => {
        this.isCreatingFortnoxInvoice = false;
        this.fetch();
      });
  }

  downloadCustomerInvoiceForm(e) {
    e.stopPropagation();
    const week = dateToWeekYear(this.bookingDate).Week;
    const year = dateToWeekYear(this.bookingDate).Year;
    const consultantName =
      this.consultantFirstName + " " + this.consultantLastName.charAt(0);

    timeReportService
      .downloadCustomerInvoiceFormPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) => {
        processDownloaded(
          result.data,
          `V${week} ${year} Fakturaformulär ${consultantName}.pdf`
        );
      });
  }

  downloadCustomerInvoiceFormByShift(e) {
    e.stopPropagation();
    const week = dateToWeekYear(this.bookingDate).Week;
    const year = dateToWeekYear(this.bookingDate).Year;
    const consultantName =
      this.consultantFirstName + " " + this.consultantLastName.charAt(0);

    timeReportService
      .downloadCustomerInvoiceFormPdfByShift(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${week} ${year} Fakturaformulär ${consultantName} (by shift).pdf`
        )
      );
  }

  downloadCustomerInvoiceFormByDate(e) {
    e.stopPropagation();
    const week = dateToWeekYear(this.bookingDate).Week;
    const year = dateToWeekYear(this.bookingDate).Year;
    const consultantName =
      this.consultantFirstName + " " + this.consultantLastName.charAt(0);

    timeReportService
      .downloadCustomerInvoiceFormPdfByDate(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${week} ${year} Fakturaformulär ${consultantName} (by date).pdf`
        )
      );
  }

  previewCustomerInvoiceForm(e) {
    e.stopPropagation();
    timeReportService
      .viewCustomerInvoiceFormPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) => processViewInNewTab(result.data));
  }

  downloadPaymentForm(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadConsultantPaymentPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Löneformulär ${d.consultantName}.pdf`
        )
      );
  }

  downloadPaymentFormByShift(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadConsultantPaymentFormPdfByShift(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Löneformulär ${d.consultantName} (by shift).pdf`
        )
      );
  }

  downloadPaymentFormByDate(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadConsultantPaymentFormPdfByDate(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Löneformulär ${d.consultantName} (by date).pdf`
        )
      );
  }

  downloadSubcontractorForm(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorFormPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Kontroll Underleverantörsfaktura ${d.consultantName}.pdf`
        )
      );
  }

  downloadSubcontractorFormByShift(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorFormPdfByShift(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Kontroll Underleverantörsfaktura ${d.consultantName} (by shift).pdf`
        )
      );
  }

  downloadSubcontractorFormByDate(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorFormPdfByDate(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Kontroll Underleverantörsfaktura ${d.consultantName} (by date).pdf`
        )
      );
  }

  downloadSubcontractorFormDraft(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorFormDraftPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Fakturautkast underleverantör ${d.consultantName} (draft).pdf`
        )
      );
  }

  downloadSubcontractorFormByShiftDraft(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorPdfDraftByShift(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Fakturautkast underleverantör ${d.consultantName} (by shift, draft).pdf`
        )
      );
  }

  downloadSubcontractorFormByDateDraft(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSubcontractorFormPdfDraftByDate(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Fakturautkast underleverantör ${d.consultantName} (by date, draft).pdf`
        )
      );
  }

  downloadSickPaymentForm(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSickPayFormPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Sjuklöneformulär ${d.consultantName}.pdf`
        )
      );
  }

  downloadSickPaymentFormByShift(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSickPayFormPdfByShift(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Sjuklöneformulär ${d.consultantName} (by shift).pdf`
        )
      );
  }

  downloadSickPaymentFormByDate(e) {
    e.stopPropagation();
    const d = this.getFormDetails();

    timeReportService
      .downloadSickPayFormPdfByDate(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) =>
        processDownloaded(
          result.data,
          `V${d.week} ${d.year} Sjuklöneformulär ${d.consultantName} (by date).pdf`
        )
      );
  }

  previewPaymentForm(e) {
    e.stopPropagation();
    timeReportService
      .viewConsultantPaymentPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) => processViewInNewTab(result.data));
  }

  previewSubcontractorForm(e) {
    e.stopPropagation();
    timeReportService
      .viewSubcontractorFormPdf({ bookingId: this.bookingId }, { responseType: "blob" })
      .then((result) => processViewInNewTab(result.data));
  }

  previewSubcontractorFormDraft(e) {
    e.stopPropagation();
    timeReportService
      .viewSubcontractorFormDraftPdf(
        { bookingId: this.bookingId },
        { responseType: "blob" }
      )
      .then((result) => processViewInNewTab(result.data));
  }

  previewSickPaymentForm(e) {
    e.stopPropagation();
    timeReportService
      .viewSickPayFormPdf(
        {
          bookingId: this.bookingId
        },
        { responseType: "blob" }
      )
      .then((result) => processViewInNewTab(result.data));
  }

  previewFortnoxUrl(id) {
    const a = document.createElement("a");
    a.target = "_blank";
    a.href = this.fortnoxInvoiceUrl(id);
    a.click();
  }

  sendSubcontractorFormDraftToConsultant() {
    return null;
  }

  async sendSubcontractFormToFortnox(e) {
    e.stopPropagation();
    this.isCreatingFortnoxInvoice = true;

    try {
      const result = await PublishSubcontractorInvoice.func({
        bookingId: this.bookingId
      });

      const { workflowId, metadata } = result.data.publishSubcontractorInvoiceWorkflow;

      globalStore().workflowTasks.addTask({
        workflowId,
        title: "Underleverantörsfaktura till Fortnox",
        bookingId: this.bookingId,
        metadata
      });
    } catch (e) {
      if (e.isAxiosError) {
        const data = e.response.data;
        globalStore().snackbars.error(`${data.name}<br>${data.description}`);
      } else {
        globalStore().snackbars.error(e.toString());
      }
    } finally {
      this.isCreatingFortnoxInvoice = false;
      this.fetch();
    }
  }

  openSubcontractorFormInFortnox() {
    openInNewWindow(this.data.subcontractFormInFortnoxUrl);
  }

  openSickForm(e) {
    e.stopPropagation();

    globalStore()
      .navigation.open(
        SICK_FORM_SLIDER + Math.random(),
        new SickFormLocation(this.bookingId)
      )
      .afterClose.on(() => {
        this.fetch();
        this.onFormClosed();
      });
  }

  saveInvoiceFormNote(value: string) {
    this.loading = true;

    timeReportService
      .saveCustomerInvoiceNote({
        bookingId: this.bookingId,
        noteDto: { note: value }
      })
      .then(() => this.fetch())
      .then(() => {
        globalStore().snackbars.success("Anteckning har sparats");
      })
      .catch((e) => {
        globalStore().snackbars.error(`Cannot save note.<br />Reason: ${e.toString()}`);
      });
  }

  savePayFormNote(value: string) {
    this.loading = true;

    timeReportService
      .saveConsultantPaymentNote({
        bookingId: this.bookingId,
        noteDto: { note: value }
      })
      .then(() => this.fetch())
      .then(() => {
        globalStore().snackbars.success("Anteckning har sparats");
      })
      .catch((e) => {
        globalStore().snackbars.error(`Cannot save note.<br />Reason: ${e.toString()}`);
      });
  }

  saveSickFormNote(value: string) {
    this.loading = true;

    timeReportService
      .saveConsultantSickPaymentNote({
        bookingId: this.bookingId,
        noteDto: { note: value }
      })
      .then(() => this.fetch())
      .then(() => {
        globalStore().snackbars.success("Anteckning har sparats");
      })
      .catch((e) => {
        globalStore().snackbars.error(`Cannot save note.<br />Reason: ${e.toString()}`);
      });
  }

  saveSubcontractorFormNote(value: string) {
    this.loading = true;

    timeReportService
      .saveSubcontractorFormNote({
        bookingId: this.bookingId,
        noteDto: { note: value }
      })
      .then(() => this.fetch())
      .then(() => {
        globalStore().snackbars.success("Anteckning har sparats");
      })
      .catch((e) => {
        globalStore().snackbars.error(`Cannot save note.<br />Reason: ${e.toString()}`);
      });
  }

  saveAdditionalPayout({
    amount,
    description
  }: {
    amount: number | null;
    description: string;
  }) {
    this.loading = true;

    return SetBookingAdditionalPayout.func({
      bookingId: this.bookingId,
      amount,
      description: description.trim() || null
    })
      .then((result) => {
        this._bookingData = result.data.setBookingAdditionalPayout;
        globalStore().snackbars.success(locale.AdditionalPayoutSaved);
      })
      .catch((e) => {
        globalStore().snackbars.error(
          `Cannot save additional payout.<br />Reason: ${e.toString()}`
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }

  openContractById(e, id: string, name?: string) {
    e.stopPropagation();
    store().navigation.open(CONTRACT_SLIDER, new ContractViewLocation(id, name));
  }

  handlePayFormWeekDay(v) {
    this.payFormWeekDay = v;
  }

  togglePayFormWeekDayDialog() {
    this.showPayFormSelectDayDialog = !this.showPayFormSelectDayDialog;
  }

  handleSickFormWeekDay(v) {
    this.sickFormWeekDay = v;
  }

  toggleSickFormWeekDayDialog() {
    this.showSickFormSelectDayDialog = !this.showSickFormSelectDayDialog;
  }

  toggleFortnoxInvoiceDialog(e) {
    e?.stopPropagation();
    this.showFortnoxInvoiceDialog = !this.showFortnoxInvoiceDialog;
  }

  confirmAction(e, action) {
    e.stopPropagation();
    store().ui.confirmNullify(locale.SendToFortnox, (confirmed) => {
      if (!confirmed) {
        return;
      }
      action(e);
    });
  }

  private getFormDetails() {
    const week = dateToWeekYear(this.bookingDate).Week;
    const year = dateToWeekYear(this.bookingDate).Year;
    const consultantName =
      this.consultantFirstName + " " + this.consultantLastName.charAt(0);

    return { week, year, consultantName };
  }
}

function checkDocumentEditEnabled(
  booking: BookingInvoicingBookingFragment | null,
  docType: "consultantPay" | "customerInvoice" | "sickPay" | "subcontractorPay"
): boolean {
  const bookingKits = compact(booking?.bookingKits);

  // enabled when no booking kits
  if (bookingKits.length === 0) {
    return true;
  }

  // enabled by default
  let enabled = true;

  const nonRejectedKits = bookingKits.filter((x) => !x.isRejected);

  // Presence of a non-rejected booking kit that includes the corresponding document
  // (sick pay, invoice, etc) blocks editing of the corresponding form.
  for (const nonRejectedKit of nonRejectedKits) {
    if (
      ![BookingKitType.AccruedCost, BookingKitType.AccruedIncome].includes(
        nonRejectedKit.type
      )
    ) {
      if (docType === "consultantPay" && nonRejectedKit.withConsultantInvoice) {
        enabled = false;
      }

      if (docType === "customerInvoice" && nonRejectedKit.withCustomerInvoice) {
        enabled = false;
      }

      if (docType === "sickPay" && nonRejectedKit.withSickPay) {
        enabled = false;
      }
    }

    // Upplupen Intäkt should block payform/subcontractor form (not sick pay form) as long as not rejected
    if (nonRejectedKit.type === BookingKitType.AccruedIncome) {
      if (docType === "consultantPay" || docType === "subcontractorPay") {
        enabled = false;
      }
    }

    // Upplupen Kostnad blocks invoice form as long as not rejected
    if (nonRejectedKit.type === BookingKitType.AccruedCost) {
      if (docType === "customerInvoice") {
        enabled = false;
      }
    }
  }

  return enabled;
}
